import { useGtm } from '@/plugins/useGtm'
import fetcher from '@/services/fetcher'
import {
  type ContentUpdateEvent,
  RealTimeEvents,
} from '@/services/real-time-events'
import type { Campaign, CmsApiResponse, Nullable } from '@/types'
import {
  createDateTimeComparator,
  mapToDateTime,
} from '@/utils/date-time-utils'
import { toApiUrl, toCmsUrl } from '@/utils/url-generation-utils'
import { DateTime } from 'luxon'
import { defineStore } from 'pinia'

type CampaignsStoreState = {
  campaigns: Map<string, Campaign>
  fetchPromise: Promise<Array<Campaign>> | null
  hasLoadedAll: boolean // Track if we've fetched all campaigns (JSON + CMS)
  isLoading: boolean // Track if campaigns are currently being fetched
  hasError: boolean // Track if there was an error fetching campaigns
  lastFetchedAt: Nullable<DateTime> // Track when data was last fetched
}

export const useCampaignsStore = defineStore('campaigns', {
  state: (): CampaignsStoreState => ({
    campaigns: new Map(),
    fetchPromise: null,
    hasLoadedAll: false,
    isLoading: false,
    hasError: false,
    lastFetchedAt: null,
  }),

  getters: {
    /**
     * Returns the next upcoming, featured campaign.
     *
     * @returns {Nullable<Campaign>}
     */
    featuredCampaign(): Nullable<Campaign> {
      return (
        this.campaignsArray.find(
          (campaign: Campaign) =>
            campaign.is_featured && campaign.close_at.diffNow().milliseconds > 0
        ) ?? null
      )
    },
    campaignsArray({ campaigns }): Array<Campaign> {
      return Array.from(campaigns.values()).sort(
        createDateTimeComparator('close_at')
      )
    },
  },

  actions: {
    /**
     * Fetches a single campaign by code.
     * Checks cache first, then tries to load JSON, then CMS as fallback.
     *
     * @param {string} campaignCode
     * @returns {Promise<Nullable<Campaign>>}
     */
    async fetchCampaign(
      campaignCode: string,
      forceRefresh: boolean = false
    ): Promise<Nullable<Campaign>> {
      // Check cache first
      if (!forceRefresh && this.campaigns.has(campaignCode)) {
        return this.campaigns.get(campaignCode) ?? null
      }

      try {
        let campaign: Campaign | null = null

        // Try JSON first (priority source) using dynamic import
        try {
          const jsonModule = await import(
            `../data/campaigns/${campaignCode}.json`
          )

          if (jsonModule.default) {
            campaign = mapToDateTime<Campaign>(jsonModule.default)

            console.error('Loaded campaign from JSON:', campaign)
          }
        } catch (jsonError) {
          console.warn(
            `File not found for ${campaignCode}, trying CMS:`,
            jsonError
          )
        }

        // If JSON didn't load, try CMS as fallback
        if (!campaign) {
          const { data, error } = await fetcher.get<CmsApiResponse<Campaign>>(
            toCmsUrl(`campaigns/${campaignCode}`)
          )

          if (error.value || !data.value?.data) {
            throw error.value || new Error('Campaign not found')
          }

          // The fetcher already handles date conversion and currency translation for CMS
          campaign = data.value.data
        }

        if (!campaign) {
          throw new Error(`Campaign ${campaignCode} could not be loaded`)
        }

        // Cache the campaign
        this.campaigns.set(campaign.code, campaign)

        return campaign
      } catch (error: any) {
        console.error('Failed to fetch campaign:', campaignCode, error)
        return null
      }
    },
    /**
     * Fetches a single campaign from the CMS by it's associated draw ID.
     *
     * @param {number} partnershipCampaignId
     * @returns {Promise<Nullable<Campaign>>}
     */
    async fetchCampaignByCampaignId(
      partnershipCampaignId: number
    ): Promise<Nullable<Campaign>> {
      // First, ensure all campaigns are loaded
      await this.fetchCampaigns()

      // TODO: Remove once all campaigns are in CMS
      if ([1777, 1783, 1859, 1869].includes(partnershipCampaignId)) {
        switch (partnershipCampaignId) {
          case 1777:
            return await this.fetchCampaign('win-an-audi-rs6-mar31')
          case 1783:
            return await this.fetchCampaign('win-an-audi-rs6-mar31')
          case 1859:
            return await this.fetchCampaign('win-a-volkswagen-golf')
          case 1869:
            return await this.fetchCampaign('win-an-audi-rs6-mar31-ref')
          default:
            return null
        }
      }

      // If not found in cache, fetch directly from CMS
      const { data, error } = await fetcher.get<CmsApiResponse<Campaign>>(
        toCmsUrl(`campaigns/by-campaign/${partnershipCampaignId}`)
      )

      if (error.value || !data.value?.data) {
        throw error.value || new Error('Campaign not found')
      }

      // The fetcher already handles date conversion and currency translation for CMS
      const campaign = data.value.data

      if (!campaign) {
        throw new Error(
          `Campaign (partnershipCampaignId: ${partnershipCampaignId}) could not be loaded`
        )
      }

      // Cache the campaign
      this.campaigns.set(campaign.code, campaign)

      return campaign
    },
    /**
     * Fetches a campaign by legacy draw ID using JSON-only mapping.
     *
     * @param {number} drawId
     * @returns {Promise<Nullable<Campaign>>}
     */
    async fetchCampaignByDrawId(drawId: number): Promise<Nullable<Campaign>> {
      await this.fetchCampaigns()

      const existingDrawIdMap: Record<number, string> = {
        563: 'win-an-audi-rs6-mar31-ref',
        608: 'win-a-volkswagen-golf',
      }

      const existingCode = existingDrawIdMap[drawId]

      return existingCode ? await this.fetchCampaign(existingCode) : null
    },
    /**
     * Load campaigns from the CMS API.
     * Returns an array of processed campaigns (does not mutate store state).
     */
    async loadFromCms(): Promise<Array<Campaign>> {
      const { data, error } = await fetcher.get<
        CmsApiResponse<Array<Campaign>>
      >(toCmsUrl('campaigns'))

      if (error.value || !data.value?.data) {
        throw error.value || new Error('Failed to fetch campaigns from CMS')
      }

      return data.value.data
    },
    /**
     * Loads campaigns from CMS.
     *
     * @param {boolean} forceRefresh - Force a full refresh of all data
     * @returns {Promise<Array<Campaign>>}
     */
    async fetchCampaigns(
      forceRefresh: boolean = false
    ): Promise<Array<Campaign>> {
      // If force refreshing, reset flags to reload everything
      if (forceRefresh) {
        this.hasLoadedAll = false
        this.campaigns = new Map()
      }

      // If we've loaded all campaigns and not forcing refresh, return cached data
      if (!forceRefresh && this.hasLoadedAll) {
        this.isLoading = false
        return this.campaignsArray
      }

      // Return the existing fetch promise to prevent multiple simultaneous calls
      if (this.fetchPromise) {
        return this.fetchPromise
      }

      // Set loading state
      this.isLoading = true
      this.hasError = false

      this.fetchPromise = (async () => {
        try {
          const cmsResult = await this.loadFromCms()

          for (const campaign of cmsResult) {
            if (!this.campaigns.has(campaign.code)) {
              this.campaigns.set(campaign.code, campaign)
            }
          }

          this.hasLoadedAll = true
          this.lastFetchedAt = DateTime.now()

          return this.campaignsArray
        } catch (error) {
          this.hasError = true
          console.error('Error fetching campaigns:', error)
          return this.campaignsArray
        } finally {
          this.isLoading = false
          this.fetchPromise = null
        }
      })()

      return this.fetchPromise
    },
    /**
     * Subscribe to real-time content updates for campaigns.
     */
    subscribeToUpdates() {
      RealTimeEvents.subscribe(
        RealTimeEvents.Channels.ContentUpdates,
        RealTimeEvents.Events.CampaignPublished,
        true,
        (event: ContentUpdateEvent<Campaign>) => {
          console.log('Campaign content update received:', event)

          if (event.action === 'deleted') {
            this.campaigns.delete(event.code)
            return
          }

          // action === 'updated'
          if (event.data) {
            // Transform broadcast data the same way the fetcher does
            const transformed = mapToDateTime<Campaign>(event.data)
            this.campaigns.set(event.code, transformed)
          } else {
            // Fallback: fetch just this one campaign from CMS
            void this.fetchCampaign(event.code, true)
          }
        }
      )
    },
    /**
     * Unsubscribe from real-time content updates.
     */
    unsubscribeFromUpdates() {
      RealTimeEvents.unsubscribe(
        RealTimeEvents.Channels.ContentUpdates,
        RealTimeEvents.Events.CampaignPublished
      )
    },
    async submitCampaign(listId: string, formData: any) {
      // Source is automatically detected from UTM params, referrer, etc.
      // No need to hardcode 'ORGANIC' - the fetcher handles intelligent source detection

      if (formData.partnership_campaign_id) {
        const { data, error } = await fetcher.post<{
          success: boolean
          redirectUrl?: string
        }>(
          toApiUrl('/subscribe-lead/create-lead/'),
          {
            list_id: listId,
            ...formData,
          },
          true, // useCredentials
          true // alwaysIncludeCredentials - forces cookies to always be sent
        )

        if (error.value) {
          throw error.value
        }

        const gtm = await useGtm()
        gtm?.trackEvent({
          event: 'lead',
        })

        return data.value
      }

      const { data, error } = await fetcher.post<{
        success: boolean
        redirectUrl?: string
      }>(
        toApiUrl('/subscribe-lead/email-phone/'),
        {
          list_id: listId,
          ...formData,
        },
        true, // useCredentials
        true // alwaysIncludeCredentials - forces cookies to always be sent
      )

      if (error.value) {
        throw error.value
      }

      const gtm = await useGtm()
      gtm?.trackEvent({
        event: 'lead',
      })

      return data.value
    },
  },
})
